import { useState } from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  CreateExternalPatientDemograhicsPullEventRequest,
  FormContextData,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import {
  COMPLETED_FORM_STATUSES,
  findFormsMerged,
  formIsVoid,
  getBaseFormTemplate,
  getFullFormName,
} from "@thalamos/aspire-common";

import { routeFns } from "../routePaths.js";
import { WorkItemStatusBox } from "./Caseload/WorkItemStatusBox.js";
import {
  Banner,
  BannerList,
  Button,
  calculateWorkItemStatus,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { HorizontalLine } from "~/components/form/index.js";
import { SubContainer } from "~/components/layout/styleWrappers.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";
import { ukLocalFormatDateTime } from "~/util.js";

export function AdmissionWarningDialog({
  existingAdmissions,
  launchAdmission,
  patientId,
  launchNewFormFn,
  patientTimeline,
  syncExternalPatientLink,
}: {
  existingAdmissions?: Omit<FormContextData, "patient">[];
  launchAdmission?: () => Promise<void>;
  patientId: string;
  patientTimeline?: PatientTimelineResponse;
  launchNewFormFn?: () => Promise<void>;
  syncExternalPatientLink?: (
    reason: CreateExternalPatientDemograhicsPullEventRequest["reason"],
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { isMobileView } = useScreenDetection();

  const [confirmStartNew, setConfirmStartNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const launchStandardForm = launchNewFormFn && launchNewFormFn;
  const launchAdmissionForm = launchAdmission && launchAdmission;

  return (
    <PopupDialog
      open={true}
      fullWidth
      maxWidth="lg"
      testId="admission-warning-dialog"
    >
      <PopupDialogTitle
        titleText={t("pages.patientHome.launchForm.admissionProgressTitle")}
        closeDialog={() => {
          navigate(routeFns.patientHome(patientId));
        }}
      />
      <Banner
        bannerType={BannerList.WARNING}
        title={t("pages.patientHome.launchForm.admissionWarningBannerTitle")}
      />
      <Box sx={{ padding: 2 }}>
        {!!existingAdmissions?.length &&
          existingAdmissions.map((existingAdmission) => {
            const merged = findFormsMerged(
              patientTimeline?.mergedPatientData,
              existingAdmission,
            );
            const completedForms = existingAdmission.forms.filter(
              (f) =>
                COMPLETED_FORM_STATUSES.includes(f.status) && !formIsVoid(f),
            );
            const hasSignedForms = completedForms.length > 0;
            const summary = calculateWorkItemStatus(theme, {
              formContextType: "admission",
              forms: existingAdmission.forms,
              otherWorkItems: existingAdmission.workItems,
            });
            return (
              <SubContainer
                key={existingAdmission.id}
                sx={{ mb: 2, background: theme.palette.background.lightGray }}
              >
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                  }}
                >
                  <FormTitle
                    titleText={t(
                      "pages.patientHome.launchForm.admissionInProgressCard.title",
                    )}
                    useReducedTopPadding={true}
                    hasTitleBottomMargin={false}
                    hasContainerMarginBottom={false}
                  />
                  {hasSignedForms && (
                    <Box sx={{ ml: 2 }}>
                      <WorkItemStatusBox
                        color={summary.textColour ?? theme.palette.common.black}
                        backgroundColor={summary.colour}
                        headingText={"Summary"}
                        mainText={summary.statusText}
                        hideHeadingText={true}
                      />
                    </Box>
                  )}
                </Box>
                {merged?.lastMerged && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: theme.spacing(1.625),
                        fontWeight: 600,
                        color: theme.palette.error.main,
                        fontStyle: "italic",
                        marginTop: theme.spacing(0.5),
                      }}
                    >
                      {t("common.merge")}:{" "}
                      {ukLocalFormatDateTime(merged.lastMerged)}
                    </Typography>
                    <Tooltip
                      title="Form(s) started in another patient, and was merged into this patient"
                      arrow
                    >
                      <IconButton>
                        <InfoOutlined
                          sx={{ color: theme.palette.error.main }}
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: isMobileView ? "column" : "row",
                    alignItems: "end",
                    background: theme.palette.common.white,
                    padding: 0.5,
                    paddingBottom: 1,
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ flex: 1, mr: 2 }}>
                    {hasSignedForms ? (
                      completedForms.map((form) => {
                        const baseFormTemplate = getBaseFormTemplate(form);
                        const fullFormName = getFullFormName(baseFormTemplate);
                        const signatures = form.versions.at(-1)?.signatures;
                        return (
                          <>
                            <Box
                              key={form.id}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography sx={{ fontWeight: 600 }}>
                                {fullFormName}
                              </Typography>
                              {signatures?.length && (
                                <Box>
                                  {signatures.map((signature) => (
                                    <Typography key={signature.userId}>
                                      {`${form.source === "manual-upload" ? "Uploaded" : "Signed"} by ${signature.userName} - ${ukLocalFormatDateTime(
                                        signature.legalSignatureDate,
                                      )}`}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                            <HorizontalLine noMargin />
                          </>
                        );
                      })
                    ) : (
                      <Typography sx={{ padding: 1 }}>
                        {t(
                          "pages.patientHome.launchForm.admissionInProgressCard.noSignedForms",
                        )}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    label={t("buttonLabels.continue")}
                    disabled={isSubmitting}
                    onClick={() => {
                      navigate(
                        routeFns.formContextPage(
                          existingAdmission.id,
                          patientId,
                        ),
                      );
                    }}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </SubContainer>
            );
          })}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmStartNew}
              onChange={(e) => {
                setConfirmStartNew(e.target.checked);
              }}
            />
          }
          label={t("pages.patientHome.launchForm.startNewAssessment")}
        />
        <Button
          variant="outlined"
          label={t("buttonLabels.startNew")}
          disabled={!confirmStartNew || isSubmitting}
          testId="start-new-button"
          onClick={() => {
            const startForm = async () => {
              setIsSubmitting(true);
              try {
                if (syncExternalPatientLink) {
                  await syncExternalPatientLink("started form");
                }

                if (launchStandardForm) {
                  await launchStandardForm();
                  return;
                }
                if (launchAdmissionForm) {
                  await launchAdmissionForm();
                  return;
                }
              } finally {
                setIsSubmitting(false);
              }
            };
            void startForm();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
